/**
 * PricingConfigCard — Admin card for a single pricing_config row.
 *
 * Two shapes supported:
 *  * v3 (flat per-tier — the current model) → single "Monthly amount" field.
 *  * v2 (legacy per-investment) → per_investment / floor / cap fields. Kept for
 *    read-only backward compatibility with any old rows still lying around.
 *
 * Everything else (Active toggle, countries editor, save/cancel) is shared.
 */
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Save, X, Plus, Shield, TrendingUp } from 'lucide-react';

const isV3 = (config) => config.pricing_version === 'v3';

const V3EditForm = ({ config, editForm, setEditForm, onSave, onCancel }) => (
  <div className="space-y-4 p-4 bg-gray-800 rounded-lg" data-testid={`pricing-edit-v3-${config.config_id}`}>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Label className="text-gray-300">Monthly amount ({config.currency})</Label>
        <Input type="number" step="0.01" value={editForm.monthly_amount ?? config.monthly_amount}
          onChange={(e) => setEditForm({ ...editForm, monthly_amount: e.target.value })}
          className="bg-gray-700 text-white border-gray-600"
          data-testid={`pricing-edit-monthly-${config.config_id}`} />
        <div className="text-[11px] text-gray-500 mt-1">Annual = monthly &times; 10 (2 months free)</div>
      </div>
      <div>
        <Label className="text-gray-300">Region name</Label>
        <Input type="text" value={editForm.region_name ?? config.region_name}
          onChange={(e) => setEditForm({ ...editForm, region_name: e.target.value })}
          className="bg-gray-700 text-white border-gray-600"
          data-testid={`pricing-edit-region-${config.config_id}`} />
      </div>
    </div>
    <div className="flex items-center space-x-2">
      <input type="checkbox" id={`edit-active-${config.config_id}`}
        checked={editForm.is_active !== undefined ? editForm.is_active : config.is_active}
        onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
        className="w-4 h-4 rounded border-gray-600" />
      <Label htmlFor={`edit-active-${config.config_id}`} className="cursor-pointer text-gray-300">Active (visible to users)</Label>
    </div>
    <div className="flex gap-2 pt-2">
      <Button onClick={() => onSave(config.config_id)} className="bg-green-500 hover:bg-green-600 text-white" data-testid={`pricing-save-${config.config_id}`}><Save className="w-4 h-4 mr-2" />Save Changes</Button>
      <Button onClick={onCancel} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700"><X className="w-4 h-4 mr-2" />Cancel</Button>
    </div>
  </div>
);

const V2LegacyDisplay = ({ config }) => (
  <div className="grid grid-cols-3 gap-4 mb-4 opacity-70">
    <div className="bg-gray-800 p-3 rounded"><div className="text-xs text-gray-400 mb-1">Per Investment</div><div className="text-xl font-bold text-gray-400">{config.symbol}{Number(config.per_investment_price).toLocaleString()}</div></div>
    <div className="bg-gray-800 p-3 rounded"><div className="text-xs text-gray-400 mb-1">Floor - Cap</div><div className="text-sm text-gray-400">{config.symbol}{Number(config.floor).toLocaleString()} - {config.symbol}{Number(config.cap).toLocaleString()}</div></div>
    <div className="bg-gray-800 p-3 rounded"><div className="text-xs text-gray-400 mb-1">Model</div><div className="text-sm text-amber-400">Legacy v2</div></div>
  </div>
);

const CountryEditor = ({ countriesList, newCountryInput, setNewCountryInput, onAddCountry, onRemoveCountry, onSave, onCancel, configId }) => (
  <div className="space-y-3">
    <div className="flex flex-wrap gap-2 p-3 bg-gray-700 rounded min-h-[100px] max-h-48 overflow-y-auto">
      {countriesList.map((country, idx) => (
        <span key={`cfg-${idx}`} className="inline-flex items-center gap-1 text-xs bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded text-white transition-colors">
          {country}
          <button onClick={() => onRemoveCountry(country)} className="ml-1 text-white hover:text-red-300 font-bold" title="Remove country">x</button>
        </span>
      ))}
      {countriesList.length === 0 && <div className="text-gray-500 text-xs">No countries yet. Add one below.</div>}
    </div>
    <div className="flex gap-2">
      <Input value={newCountryInput} onChange={(e) => setNewCountryInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAddCountry(); } }}
        placeholder="Type country name and press Enter" className="bg-gray-700 text-white border-gray-600 text-sm flex-1" />
      <button type="button" onClick={onAddCountry} className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm flex items-center gap-1 transition-colors">
        <Plus className="w-4 h-4" />Add
      </button>
    </div>
    <div className="flex gap-2 pt-2">
      <Button size="sm" onClick={() => onSave(configId)} className="bg-green-500 hover:bg-green-600 text-white"><Save className="w-4 h-4 mr-2" />Save Countries</Button>
      <Button size="sm" variant="outline" onClick={onCancel} className="border-gray-600 text-gray-300 hover:bg-gray-700"><X className="w-4 h-4 mr-2" />Cancel</Button>
    </div>
  </div>
);

const V3Display = ({ config }) => {
  const monthly = Number(config.monthly_amount || 0);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
      <div className="bg-gray-800 p-3 rounded">
        <div className="text-xs text-gray-400 mb-1">Monthly</div>
        <div className="text-xl font-bold text-green-400">{config.symbol}{monthly.toLocaleString()}</div>
      </div>
      <div className="bg-gray-800 p-3 rounded">
        <div className="text-xs text-gray-400 mb-1">Annual (10&times; monthly)</div>
        <div className="text-lg font-semibold text-purple-400">{config.symbol}{(monthly * 10).toLocaleString()}</div>
      </div>
      <div className="bg-gray-800 p-3 rounded col-span-2 sm:col-span-1">
        <div className="text-xs text-gray-400 mb-1">Currency</div>
        <div className="text-lg font-semibold text-blue-400">{config.currency}</div>
      </div>
    </div>
  );
};

const CountriesBlock = ({ config, isManagingCountries, countriesList, newCountryInput, setNewCountryInput, onAddCountry, onRemoveCountry, onSaveCountries, onCancelCountries, onManageCountries }) => (
  <div className="bg-gray-800 p-3 rounded">
    <div className="flex justify-between items-center mb-2">
      <div className="text-xs text-gray-400">Countries ({config.countries?.length || 0}):</div>
      {!isManagingCountries && (
        <Button size="sm" onClick={() => onManageCountries(config)} className="bg-green-500 hover:bg-green-600 text-white text-xs h-7" data-testid={`pricing-manage-countries-${config.config_id}`}>Manage Countries</Button>
      )}
    </div>
    {isManagingCountries ? (
      <CountryEditor countriesList={countriesList} newCountryInput={newCountryInput} setNewCountryInput={setNewCountryInput}
        onAddCountry={onAddCountry} onRemoveCountry={onRemoveCountry} onSave={onSaveCountries} onCancel={onCancelCountries} configId={config.config_id} />
    ) : (
      <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
        {(config.countries || []).map((country, idx) => (
          <span key={`cfg-${idx}`} className="text-xs bg-gray-700 px-2 py-1 rounded text-gray-300">{country}</span>
        ))}
      </div>
    )}
  </div>
);

const cardTitle = (config) => (
  isV3(config)
    ? `${config.region_name || 'Region'} (${config.currency})`
    : (config.category_name?.includes(config.currency) ? config.category_name : `${config.category_name || `Category ${config.category}`} (${config.currency})`)
);

const TierBadge = ({ config }) => {
  if (!isV3(config)) return null;
  const isCont = config.tier === 'premium_continuity';
  const Icon = isCont ? Shield : TrendingUp;
  const cls = isCont ? 'bg-blue-500/20 text-blue-300 border-blue-500/60' : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/60';
  return (
    <Badge variant="outline" className={`text-xs font-semibold ${cls}`} data-testid={`pricing-tier-badge-${config.config_id}`}>
      <Icon className="w-3 h-3 mr-1" />
      {isCont ? 'Continuity System' : 'Portfolio Clarity'}
    </Badge>
  );
};

export const PricingConfigCard = ({
  config, isEditing, editForm, setEditForm, onSaveEdit, onCancelEdit, onEdit, onDelete,
  isManagingCountries, countriesList, newCountryInput, setNewCountryInput,
  onAddCountry, onRemoveCountry, onSaveCountries, onCancelCountries, onManageCountries
}) => (
  <Card className="bg-gray-700 border-blue-500/30" data-testid={`pricing-card-${config.config_id}`}>
    <CardHeader>
      <CardTitle className="flex flex-wrap items-center justify-between text-white gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <span>{cardTitle(config)}</span>
          <TierBadge config={config} />
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={config.is_active ? 'success' : 'secondary'}>{config.is_active ? 'Active' : 'Inactive'}</Badge>
          {!isEditing && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => onEdit(config)} className="border-blue-500 text-blue-400 hover:bg-blue-500/10" data-testid={`pricing-edit-btn-${config.config_id}`}><Edit className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => onDelete(config.config_id)} className="border-red-500 text-red-400 hover:bg-red-500/10" data-testid={`pricing-delete-btn-${config.config_id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          )}
        </div>
      </CardTitle>
      <CardDescription className="text-gray-400">{config.countries?.length || 0} countries supported</CardDescription>
    </CardHeader>
    <CardContent>
      {isEditing ? (
        isV3(config)
          ? <V3EditForm config={config} editForm={editForm} setEditForm={setEditForm} onSave={onSaveEdit} onCancel={onCancelEdit} />
          : <V2LegacyDisplay config={config} />
      ) : (
        <>
          {isV3(config) ? <V3Display config={config} /> : <V2LegacyDisplay config={config} />}
          <CountriesBlock config={config} isManagingCountries={isManagingCountries} countriesList={countriesList}
            newCountryInput={newCountryInput} setNewCountryInput={setNewCountryInput}
            onAddCountry={onAddCountry} onRemoveCountry={onRemoveCountry}
            onSaveCountries={onSaveCountries} onCancelCountries={onCancelCountries} onManageCountries={onManageCountries} />
        </>
      )}
    </CardContent>
  </Card>
);
